import { Layer, ProgressIndicator, ThemeProvider, initializeIcons, registerIcons } from '@fluentui/react';
import 'core-js/features/array/flat';
import 'core-js/stable';
import { configure, reaction } from 'mobx';
import { Provider, observer } from 'mobx-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Route, Router } from 'react-router-dom';
import 'regenerator-runtime/runtime';
import { requestEmmiter } from 'src/api';
import { ws } from 'src/api/ws';
import { loadBrandedTheme, loadDefaultTheme } from 'src/brands';
import 'src/commands/commands';
import { EVENT_REQUEST_PROGRESS } from 'src/constants';
import RootContainer from 'src/containers/RootContainer';
import 'src/functions/functions';
import history from 'src/history';
import office from 'src/services/office';
import { runShortAction } from 'src/services/utils';
import * as stores from 'src/stores';
import 'src/v2/services/runtime/service-register';
import {DialogContext} from "@v2/components/dialogs/DialogContext/DialogContext";
import {clearObjectTypesCache} from "@v2/services/flowsteps/remote-api-provider";

initializeIcons(undefined, { disableWarnings: true });

registerIcons({
  fontFace: {
    fontFamily: `"xappex-symbols"`
  },
  icons: {
    'xappex-worksheet': '\uE909',
    'xappex-target': '\uE90a',
    'xappex-run-flowstep': '\uE908',
    'xappex-run-all-flowsteps': '\uE90b',
    'xappex-refresh-all-datasources': '\uE90c',
    'xappex-new-flowstep': '\uE90d',
    'xappex-new-datasource': '\uE90e',
    'xappex-edit': '\uE90f',
    'xappex-current-worksheet': '\uE910',
    'xappex-all-worksheets': '\uE911',
    'xappex-about': '\uE900',
    'xappex-help': '\uE901',
    'xappex-license': '\uE902',
    'xappex-schedules': '\uE903',
    'xappex-settings': '\uE904',
    'xappex-share': '\uE905',
    'xappex-signout': '\uE906',
    'xappex-source': '\uE907',
    'xappex-refresh-datasource': '\uE912',
    'xappex-delete': '\uE913',
  }
});

const defaultTheme = loadDefaultTheme();


//Remove this line when we move all observable to TS accessors observables
configure({
  enforceActions: 'never',
})

const App = ({ initializeError }) => {
  const disposer = useRef();
  const { userStore, officeStore } = stores;

  const [theme, setTheme] = useState(userStore.brand ? loadBrandedTheme(userStore.brand) : defaultTheme);
  const [requestInProgress, setRequestInProgress] = useState(false);
  const handleRequestActivity = useCallback(status => setRequestInProgress(status));

  function visibilityModeChanged({ visibilityMode }) {
    office.setVisibilityMode(visibilityMode === Office.VisibilityMode.taskpane);
  }

  useEffect(() => {
    requestEmmiter.addListener(EVENT_REQUEST_PROGRESS, handleRequestActivity);

    runShortAction(async () => {
      await office.startupTaskpane();
      disposer.current = await Office.addin.onVisibilityModeChanged(visibilityModeChanged);

      if (initializeError) { throw initializeError }
    });

    const startupDisposer = reaction(
      () => userStore.userId,
      () => runShortAction(async () => {
        setTheme(userStore.brand ? loadBrandedTheme(userStore.brand) : defaultTheme)
        officeStore.initialized = false;
        if (userStore.userName) {
          await officeStore.clean();
          clearObjectTypesCache();
          if (userStore.isAuthorized) {
            await officeStore.startup();
            userStore.checkLicenseStatus();
          }
        }
        ws.connect(userStore.token);
      }),
      {
        fireImmediately: true
      }
    )

    return () => {
      disposer.current && disposer.current();
      startupDisposer();
      requestEmmiter.removeListener(EVENT_REQUEST_PROGRESS, handleRequestActivity);
    }
  }, []);



  return (
    <ThemeProvider theme={theme} className="height-100">
      {(requestInProgress) &&
        <Layer>
          <ProgressIndicator barHeight={3} styles={{ itemProgress: { padding: 0, } }} />
        </Layer>
      }
      <DialogContext/>
      <Provider {...stores}>
        <Router basename='/' history={history}>
          <Route component={RootContainer} />
        </Router>
      </Provider>
    </ThemeProvider>
  );
}

export default observer(App);
